import {Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {PdfColorSettings} from "../../../models/pdf-color-settings.model";
import {ColorEvent} from "ngx-color";
import {PdfColorSettingsService} from "../../../services/model/pdf-color-settings.service";
import {IrfNotificationService} from "irf-services";
import {NxDialogService, NxModalRef} from "@aposin/ng-aquila/modal";
import {AddImageModalInput, AddImageToModelComponent} from "../../modal/add-image-to-model/add-image-to-model.component";
import {Utilities} from "../../../utilities/utilities";
import {AttachmentService} from "../../../services/model/attachment.service";

@Component({
  selector: 'forms-pdf-color-settings-form',
  templateUrl: './pdf-color-settings-form.component.html',
  styleUrls: ['./pdf-color-settings-form.component.scss']
})
export class PdfColorSettingsFormComponent implements OnInit, OnChanges {

  @Input() partnerId: number;
  @Output() settingsSaved: EventEmitter<PdfColorSettings> = new EventEmitter<PdfColorSettings>();

  settings: PdfColorSettings;
  componentDialogRef!: NxModalRef<any>;

  colorFields = [
    {key: 'coverPageFontColor', section: 'coverPage', label: 'pdfColors.coverPage.fontColor'},
    {key: 'tocSeparatorColor', section: 'toc', label: 'pdfColors.toc.separatorColor'},
    {key: 'tocFontColor', section: 'toc', label: 'pdfColors.toc.fontColor'},
    {key: 'chartSidebarFontColor', section: 'chartSidebar', label: 'pdfColors.chartSidebar.fontColor'},
    {key: 'chartSidebarSeparatorColor', section: 'chartSidebar', label: 'pdfColors.chartSidebar.separatorColor'},
    {key: 'chartBoxBackgroundColor', section: 'chartBox', label: 'pdfColors.chartBox.backgroundColor'},
    {key: 'chartBoxFontColor', section: 'chartBox', label: 'pdfColors.chartBox.fontColor'},
    {key: 'footerPageNumberColor', section: 'footer', label: 'pdfColors.footer.pageNumberColor'},
    {key: 'footerDataColor', section: 'footer', label: 'pdfColors.footer.dataColor'},
    {key: 'backgroundOverlayColor', section: 'general', label: 'pdfColors.general.backgroundOverlayColor'}
  ];

  activeColorPicker: string | null = null;
  openUpward: boolean = false;
  saving: boolean = false;
  loading: boolean = true;

  private readonly UPWARD_SECTIONS = ['footer', 'general'];

  constructor(
    private pdfColorSettingsService: PdfColorSettingsService,
    private notificationService: IrfNotificationService,
    private dialogService: NxDialogService,
    private attachmentService: AttachmentService,
    private elementRef: ElementRef
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.activeColorPicker) {
      const target = event.target as HTMLElement;
      const colorPickerContainers = this.elementRef.nativeElement.querySelectorAll('.color-picker-container');
      const colorPreviews = this.elementRef.nativeElement.querySelectorAll('.color-preview');

      let clickedInside = false;
      colorPickerContainers.forEach((container: HTMLElement) => {
        if (container.contains(target)) clickedInside = true;
      });
      colorPreviews.forEach((preview: HTMLElement) => {
        if (preview.contains(target)) clickedInside = true;
      });

      if (!clickedInside) {
        this.activeColorPicker = null;
      }
    }
  }

  ngOnInit(): void {
    if (this.partnerId) {
      this.loadSettings();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['partnerId'] && this.partnerId) {
      this.loadSettings();
    }
  }

  private loadSettings(): void {
    this.loading = true;
    this.pdfColorSettingsService.getForPartner(this.partnerId).subscribe({
      next: (settings) => {
        this.settings = {...settings};
        this.loadImages();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  private loadImages(): void {
    if (this.settings.backgroundImageId) {
      this.attachmentService.repo.getById(this.settings.backgroundImageId.toString()).subscribe(attachment => {
        this.settings.backgroundImage = this.attachmentService.addExtendedPropertiesForModel(attachment);
      });
    }
    if (this.settings.logoImageId) {
      this.attachmentService.repo.getById(this.settings.logoImageId.toString()).subscribe(attachment => {
        this.settings.logoImage = this.attachmentService.addExtendedPropertiesForModel(attachment);
      });
    }
  }

  changeColor(key: string, $event: ColorEvent): void {
    this.settings[key] = $event.color.hex;
  }

  toggleColorPicker(key: string): void {
    if (this.activeColorPicker === key) {
      this.activeColorPicker = null;
    } else {
      const field = this.colorFields.find(f => f.key === key);
      this.openUpward = field ? this.UPWARD_SECTIONS.includes(field.section) : false;
      this.activeColorPicker = key;
    }
  }

  resetToDefaults(): void {
    this.pdfColorSettingsService.getDefaults().subscribe(defaults => {
      Object.assign(this.settings, defaults);
    });
  }

  saveSettings(): void {
    this.saving = true;
    this.pdfColorSettingsService.saveForPartner(this.partnerId, this.settings).subscribe({
      next: (saved) => {
        this.saving = false;
        this.settingsSaved.emit(saved);
        this.notificationService.showSuccess('general.success', 'pdfColors.saveSuccess');
      },
      error: () => {
        this.saving = false;
        this.notificationService.showError('general.error', 'pdfColors.saveError');
      }
    });
  }

  getFieldsBySection(section: string): any[] {
    return this.colorFields.filter(f => f.section === section);
  }

  openBackgroundImageModal(): void {
    const model = {attachment: this.settings.backgroundImage};
    this.componentDialogRef = this.dialogService.open(
      AddImageToModelComponent,
      Utilities.getDefaultModalConfig(new AddImageModalInput(model))
    );
    this.componentDialogRef.afterClosed().subscribe(result => {
      if (result && result !== 'cancel') {
        this.settings.backgroundImageId = result.id;
        this.settings.backgroundImage = result.srcWidth ? result : this.attachmentService.addExtendedPropertiesForModel(result);
      }
    });
  }

  openLogoImageModal(): void {
    const model = {attachment: this.settings.logoImage};
    this.componentDialogRef = this.dialogService.open(
      AddImageToModelComponent,
      Utilities.getDefaultModalConfig(new AddImageModalInput(model))
    );
    this.componentDialogRef.afterClosed().subscribe(result => {
      if (result && result !== 'cancel') {
        this.settings.logoImageId = result.id;
        this.settings.logoImage = result.srcWidth ? result : this.attachmentService.addExtendedPropertiesForModel(result);
      }
    });
  }

  removeBackgroundImage(): void {
    this.settings.backgroundImageId = null;
    this.settings.backgroundImage = null;
  }

  removeLogoImage(): void {
    this.settings.logoImageId = null;
    this.settings.logoImage = null;
  }
}
